/**
 * OnboardingRouter
 *
 * Manages routing between onboarding steps and persists progress.
 * This component:
 * 1. Captures UTM params and attribution data on entry
 * 2. Routes between onboarding steps
 * 3. Persists progress to database on each step completion
 * 4. Handles resume from abandoned onboarding
 */

import { useEffect, useCallback } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { logger } from '../../lib/logger';
import type { OnboardingData, OnboardingStep } from '../../lib/supabase';
import {
  currentOnboardingStepAtom,
  onboardingDataAtom,
  onboardingLoadingAtom,
  onboardingErrorAtom,
  hasActiveSubscriptionAtom,
  completeStepAtom,
} from '../../state/onboardingAtoms';

// Import step components
import { WelcomeStep } from './steps/WelcomeStep';
import { QuestionsStep } from './steps/QuestionsStep';
import { StagingStep } from './steps/StagingStep';
import { ResultStep } from './steps/ResultStep';
import { PersonalizeStep } from './steps/PersonalizeStep';
import { ValueStep } from './steps/ValueStep';
import { SuccessStep } from './steps/SuccessStep';

/**
 * Capture UTM parameters and attribution data from URL
 */
function captureAttribution(): Partial<OnboardingData> {
  const searchParams = new URLSearchParams(window.location.search);

  // Detect device type
  const userAgent = navigator.userAgent.toLowerCase();
  let deviceType: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (/mobile|android|iphone|ipod/.test(userAgent)) {
    deviceType = 'mobile';
  } else if (/tablet|ipad/.test(userAgent)) {
    deviceType = 'tablet';
  }

  return {
    utm_source: searchParams.get('utm_source') || undefined,
    utm_medium: searchParams.get('utm_medium') || undefined,
    utm_campaign: searchParams.get('utm_campaign') || undefined,
    utm_term: searchParams.get('utm_term') || undefined,
    utm_content: searchParams.get('utm_content') || undefined,
    referrer_url: document.referrer || undefined,
    device_type: deviceType,
  };
}

/**
 * Map route path to step name
 */
function pathToStep(path: string): OnboardingStep {
  const stepMap: Record<string, OnboardingStep> = {
    '/onboarding/welcome': 'welcome',
    '/onboarding/questions': 'questions',
    '/onboarding/stage': 'stage',
    '/onboarding/result': 'result',
    '/onboarding/personalize': 'personalize',
    '/onboarding/value': 'value',
    '/onboarding/success': 'success',
  };
  return stepMap[path] || 'welcome';
}

/**
 * Map step name to route path
 */
function stepToPath(step: OnboardingStep): string {
  return `/onboarding/${step}`;
}

export function OnboardingRouter() {
  const { user, profile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentStep, setCurrentStep] = useAtom(currentOnboardingStepAtom);
  const [onboardingData, setOnboardingData] = useAtom(onboardingDataAtom);
  const setLoading = useSetAtom(onboardingLoadingAtom);
  const setError = useSetAtom(onboardingErrorAtom);
  const hasActiveSubscription = useAtomValue(hasActiveSubscriptionAtom);
  const completeStep = useSetAtom(completeStepAtom);

  // Initialize onboarding data on mount
  useEffect(() => {
    if (!user || !profile) return;

    const initializeOnboarding = async () => {
      setLoading(true);

      try {
        // Check if there's existing onboarding data to resume
        const existingData = profile.onboarding_data as OnboardingData | null;

        if (existingData && existingData.session_id) {
          // Resume from existing data
          logger.info('[OnboardingRouter] Resuming onboarding', {
            lastStep: existingData.last_completed_step,
          });

          setOnboardingData(existingData);

          // If there's a last completed step, navigate to the next step
          if (existingData.last_completed_step) {
            const steps: OnboardingStep[] = [
              'welcome',
              'questions',
              'stage',
              'result',
              'personalize',
            ];
            const lastIndex = steps.indexOf(existingData.last_completed_step);
            if (lastIndex >= 0 && lastIndex < steps.length - 1) {
              const nextStep = steps[lastIndex + 1];
              setCurrentStep(nextStep);
              navigate(stepToPath(nextStep), { replace: true });
            }
          }
        } else {
          // New onboarding - capture attribution data
          const attribution = captureAttribution();
          const newData: OnboardingData = {
            session_id: crypto.randomUUID(),
            started_at: new Date().toISOString(),
            steps_completed: [],
            ...attribution,
          };

          setOnboardingData(newData);

          // Persist initial data
          await persistOnboardingData(user.id, newData);

          logger.info('[OnboardingRouter] Started new onboarding', {
            sessionId: newData.session_id,
            attribution,
          });
        }
      } catch (error) {
        logger.error('[OnboardingRouter] Error initializing onboarding', error);
        setError('Failed to initialize onboarding. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeOnboarding();
  }, [user, profile?.id]); // Only depend on profile.id to prevent re-runs

  // Sync current step with route
  useEffect(() => {
    const step = pathToStep(location.pathname);
    setCurrentStep(step);
  }, [location.pathname, setCurrentStep]);

  /**
   * Persist onboarding data to database
   */
  const persistOnboardingData = useCallback(
    async (userId: string, data: OnboardingData) => {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({
            onboarding_data: data,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId);

        if (error) {
          throw error;
        }

        logger.info('[OnboardingRouter] Persisted onboarding data', {
          lastStep: data.last_completed_step,
        });
      } catch (error) {
        logger.error('[OnboardingRouter] Failed to persist onboarding data', error);
        throw error;
      }
    },
    []
  );

  /**
   * Complete the current step and navigate to the next
   */
  const handleStepComplete = useCallback(
    async (
      completedStep: OnboardingStep,
      additionalData?: Partial<OnboardingData>
    ) => {
      if (!user) return;

      setLoading(true);

      try {
        // Update local state
        completeStep(completedStep);

        // Merge additional data
        const updatedData: OnboardingData = {
          ...onboardingData,
          ...additionalData,
          last_completed_step: completedStep,
          steps_completed: [
            ...(onboardingData.steps_completed || []),
            completedStep,
          ].filter((v, i, a) => a.indexOf(v) === i), // Dedupe
        };

        setOnboardingData(updatedData);

        // Persist to database
        await persistOnboardingData(user.id, updatedData);

        // Determine next step
        const nextStep = getNextStep(completedStep, hasActiveSubscription);

        if (nextStep) {
          setCurrentStep(nextStep);
          navigate(stepToPath(nextStep));
        } else {
          // Onboarding complete - mark as completed and go to dashboard
          await completeOnboarding(user.id, updatedData);
        }
      } catch (error) {
        logger.error('[OnboardingRouter] Error completing step', error);
        setError('Failed to save progress. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [
      user,
      onboardingData,
      hasActiveSubscription,
      navigate,
      completeStep,
      setCurrentStep,
      setOnboardingData,
      setLoading,
      setError,
      persistOnboardingData,
    ]
  );

  /**
   * Get the next step based on current step and user state
   */
  const getNextStep = (
    currentStep: OnboardingStep,
    hasSubscription: boolean
  ): OnboardingStep | null => {
    const stepFlow: OnboardingStep[] = [
      'welcome',
      'questions',
      'stage',
      'result',
      'personalize',
    ];

    const currentIndex = stepFlow.indexOf(currentStep);

    if (currentIndex === -1) {
      // Current step is value or success - onboarding complete
      return null;
    }

    if (currentIndex < stepFlow.length - 1) {
      // Move to next step in flow
      return stepFlow[currentIndex + 1];
    }

    // After personalize, go to value (free) or success (paid)
    return hasSubscription ? 'success' : 'value';
  };

  /**
   * Mark onboarding as complete and redirect to dashboard
   */
  const completeOnboarding = useCallback(
    async (userId: string, finalData: OnboardingData) => {
      try {
        const completedData: OnboardingData = {
          ...finalData,
          completed_at: new Date().toISOString(),
        };

        const { error } = await supabase
          .from('profiles')
          .update({
            onboarding_completed: true,
            onboarding_completed_at: new Date().toISOString(),
            onboarding_data: completedData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId);

        if (error) {
          throw error;
        }

        // Refresh profile to update auth context
        await refreshProfile();

        logger.info('[OnboardingRouter] Onboarding completed', {
          sessionId: finalData.session_id,
          timeToFirstStage: finalData.time_to_first_stage_seconds,
        });

        // Navigate to dashboard
        navigate('/dashboard', { replace: true });
      } catch (error) {
        logger.error('[OnboardingRouter] Failed to complete onboarding', error);
        // Still navigate to dashboard on error - better than stuck on onboarding
        navigate('/dashboard', { replace: true });
      }
    },
    [navigate, refreshProfile]
  );

  /**
   * Skip onboarding and go directly to dashboard
   * Only available after first staging is complete
   */
  const handleSkip = useCallback(async () => {
    if (!user) return;

    try {
      const skipData: OnboardingData = {
        ...onboardingData,
        drop_off_step: currentStep,
        completed_at: new Date().toISOString(),
      };

      await completeOnboarding(user.id, skipData);
    } catch (error) {
      logger.error('[OnboardingRouter] Error skipping onboarding', error);
      navigate('/dashboard', { replace: true });
    }
  }, [user, onboardingData, currentStep, completeOnboarding, navigate]);

  // Context value for step components
  const stepProps = {
    onStepComplete: handleStepComplete,
    onSkip: handleSkip,
    onboardingData,
  };

  return (
    <Routes>
      <Route path="welcome" element={<WelcomeStep {...stepProps} />} />
      <Route path="questions" element={<QuestionsStep {...stepProps} />} />
      <Route path="stage" element={<StagingStep {...stepProps} />} />
      <Route path="result" element={<ResultStep {...stepProps} />} />
      <Route path="personalize" element={<PersonalizeStep {...stepProps} />} />
      <Route path="value" element={<ValueStep {...stepProps} />} />
      <Route path="success" element={<SuccessStep {...stepProps} />} />

      {/* Default redirect to welcome */}
      <Route path="*" element={<Navigate to="welcome" replace />} />
    </Routes>
  );
}

/**
 * Export step props type for use in step components
 */
export interface OnboardingStepProps {
  onStepComplete: (
    step: OnboardingStep,
    additionalData?: Partial<OnboardingData>
  ) => Promise<void>;
  onSkip: () => Promise<void>;
  onboardingData: OnboardingData;
}
